import { createTheme } from '@mui/material';
import variable from './shared/style/variable';

declare module '@mui/material' {
    interface ButtonPropsVariantOverrides {
        orange: true;
        greenLight: true;
        green: true;
        white: true;
    }
    interface StackOwnProps {
        variant?: string;
    }
    interface CardOwnProps {
        variant?: string;
    }
    interface TableCellPropsVariantOverrides {
        result: true;
    }
    interface ContainerOwnProps {
        variant?: string;
    }
    interface TabOwnProps {
        variant?: string;
    }
    interface TableHeadOwnProps {
        variant?: string;
    }
    interface TableRowOwnProps {
        variant?: string;
    }
    interface SvgIconPropsColorOverrides {
        orange: true;
        white: true;
    }
}

export const theme = createTheme({
    breakpoints: {
        values: {
            xs: 0,
            sm: 576,
            md: 768,
            lg: 992,
            xl: 1200,
        },
    },
    components: {
        MuiContainer: {
            variants: [
                {
                    props: { variant: 'body' },
                    style: {
                        padding: '0!important',
                        height: '100vh',
                        display: 'flex',
                        flexDirection: 'column',
                    },
                },
            ],
        },
        MuiStack: {
            variants: [
                {
                    props: { variant: 'header' },
                    style: {
                        backgroundColor: variable.green,
                        height: '6rem',
                    },
                },
                {
                    props: { variant: 'line' },
                    style: {
                        backgroundColor: variable.purple,
                        height: '1rem',
                        width: '100%',
                    },
                },
                {
                    props: { variant: 'action' },
                    style: {
                        backgroundColor: variable.greyLight,
                        height: '2.5rem',
                    },
                },
                {
                    props: { variant: 'stepper' },
                    style: {
                        backgroundColor: variable.white,
                    },
                },
                {
                    props: { variant: 'footer' },
                    style: {
                        color: variable.greenLight,
                        userSelect: 'none',
                    },
                },
                {
                    props: { variant: 'scrollable' },
                    style: {
                        position: 'absolute',
                        overflowY: 'auto',
                        overflowX: 'hidden',
                        height: '100%',
                        width: '100%',
                        '&::-webkit-scrollbar': {
                            width: '0.3rem',
                            height: '0.3rem',
                        },
                        '&::-webkit-scrollbar-track': {
                            boxShadow: 'inset 0 0 0.3rem grey',
                            borderRadius: '0.3rem',
                        },
                        '&::-webkit-scrollbar-thumb': {
                            background: variable.greenLight,
                            borderRadius: '0.3rem',
                        },
                        '&::-webkit-scrollbar-thumb:hover': {
                            background: variable.greenLight,
                        },
                    },
                },
                {
                    props: { variant: 'actionEngine' },
                    style: {
                        backgroundColor: variable.green,
                        userSelect: 'none',
                    },
                },
                {
                    props: { variant: 'modal' },
                    style: {
                        backgroundColor: variable.white,
                        border: `0.1rem solid ${variable.greenLight}`,
                        borderRadius: '1rem',
                        padding: '1.5rem',
                        minHeight: '40rem',
                    },
                },
                {
                    props: { variant: 'modal-small' },
                    style: {
                        backgroundColor: variable.white,
                        border: `0.1rem solid ${variable.greenLight}`,
                        borderRadius: '1rem',
                        padding: '1.5rem',
                        minHeight: '6rem',
                    },
                },
                {
                    props: { variant: 'bottomLine' },
                    style: {
                        borderBottom: `0.1rem solid ${variable.greyLight}`,
                    },
                },
                {
                    props: { variant: 'bottomLineGreen' },
                    style: {
                        borderBottom: `0.2rem solid ${variable.greenLight}`,
                        padding: '0.5rem 0',
                    },
                },
                {
                    props: { variant: 'alert' },
                    style: {
                        backgroundColor: variable.red,
                        color: variable.white,
                    },
                },
            ],
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                    borderRadius: '0.5rem',
                    padding: '0.4rem 0.8rem',
                    '& .MuiButton-startIcon': {
                        marginTop: '-0.1rem',
                    },
                },
            },
            variants: [
                {
                    props: { variant: 'orange' },
                    style: {
                        backgroundColor: variable.orange,
                        color: variable.white,
                        '&:hover': {
                            backgroundColor: variable.red,
                        },
                    },
                },
                {
                    props: { variant: 'greenLight' },
                    style: {
                        color: variable.greenLight,
                    },
                },
                {
                    props: { variant: 'green' },
                    style: {
                        backgroundColor: variable.green,
                        color: variable.white,
                        padding: '0.4rem 1rem',
                        textTransform: 'uppercase',
                        '&:hover': {
                            backgroundColor: variable.greenLight,
                        },
                    },
                },
                {
                    props: { variant: 'white' },
                    style: {
                        backgroundColor: variable.white,
                        padding: '0.4rem 1rem',
                        border: `0.1rem solid ${variable.greyLight}`,
                        '&:hover': {
                            backgroundColor: variable.greenLight,
                        },
                    },
                },
            ],
        },
        MuiStepButton: {
            styleOverrides: {
                root: {
                    padding: '1rem',
                },
            },
        },
        MuiPagination: {
            styleOverrides: {
                root: {
                    '& .Mui-selected': {
                        backgroundColor: variable.green,
                        color: variable.white,
                    },
                },
            },
        },
        MuiPaginationItem: {
            styleOverrides: {
                root: { color: variable.green, border: 0 },
            },
        },
        MuiStep: {
            styleOverrides: {
                root: {
                    '& .MuiStepIcon-root.Mui-active': {
                        color: variable.greenLight,
                    },
                },
            },
        },
        MuiTable: {
            styleOverrides: {
                root: {
                    '& .MuiTableRow-hover': {
                        cursor: 'pointer',
                    },
                },
            },
        },
        MuiTableCell: {
            variants: [
                {
                    props: { variant: 'result' },
                    style: {
                        padding: '0.5rem 0rem',
                    },
                },
            ],
        },
        MuiInputLabel: {
            styleOverrides: {
                root: {
                    fontSize: '1rem',
                },
            },
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    '& .MuiInputBase-input': {
                        padding: '0.3rem 0.8rem',
                    },
                    '& input[type=number]': {
                        '-moz-appearance': 'textfield',
                    },
                    '& input[type=number]::-webkit-outer-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0,
                    },
                    '& input[type=number]::-webkit-inner-spin-button': {
                        '-webkit-appearance': 'none',
                        margin: 0,
                    },
                },
            },
        },
        MuiFormHelperText: { styleOverrides: { root: { marginLeft: 0 } } },
        MuiCard: {
            variants: [
                {
                    props: { variant: 'standard' },
                    style: {
                        cursor: 'pointer',
                        '&:hover': {
                            boxShadow: `0 0 0.7rem ${variable.greenLight}`,
                            color: variable.greenLight,
                        },
                    },
                },
                {
                    props: { variant: 'selected' },
                    style: {
                        cursor: 'pointer',
                        boxShadow: `0 0 0.7rem ${variable.greenLight}`,
                        color: variable.greenLight,
                    },
                },
            ],
        },
        MuiAccordionSummary: {
            styleOverrides: {
                root: {
                    color: variable.green,
                    backgroundColor: variable.greyLight,
                    '& .MuiSvgIcon-root': {
                        color: variable.orange,
                    },
                },
            },
        },
        MuiAccordion: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                },
            },
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    textTransform: 'none',
                },
            },
            variants: [
                {
                    props: { variant: 'standard' },
                    style: {
                        color: `${variable.green}!important`,
                    },
                },
                {
                    props: { variant: 'warning' },
                    style: {
                        color: `${variable.red}!important`,
                    },
                },
            ],
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    '& .MuiTabs-indicator': {
                        backgroundColor: variable.greenLight,
                        height: 5,
                    },
                },
            },
        },
        MuiSvgIcon: {
            variants: [
                {
                    props: { color: 'orange' },
                    style: {
                        color: variable.orange,
                    },
                },
                {
                    props: { color: 'white' },
                    style: {
                        color: variable.white,
                    },
                },
            ],
        },
        MuiCircularProgress: {
            styleOverrides: {
                root: {
                    color: variable.green,
                    margin: '4rem auto!important',
                    alignItems: 'center',
                },
            },
        },
        MuiListItemButton: {
            styleOverrides: {
                root: {
                    '&:hover, &.Mui-selected, &.Mui-selected:hover': {
                        color: variable.greyLight,
                        backgroundColor: variable.green,
                    },
                    borderBottom: `0.1rem solid ${variable.greyLight}`,
                },
            },
        },
        MuiModal: {
            styleOverrides: {
                root: {
                    zIndex: 1000,
                },
            },
        },
        MuiStepper: {
            styleOverrides: {
                root: {
                    width: '100%!important',
                },
            },
        },
        MuiCardMedia: {
            styleOverrides: {
                root: {
                    objectFit: 'initial',
                },
            },
        },
        MuiCardHeader: {
            styleOverrides: {
                root: {
                    height: '4rem',
                },
            },
        },
        MuiTableContainer: {
            styleOverrides: {
                root: {
                    overflow: 'visible',
                },
            },
        },
        MuiTableHead: {
            variants: [
                {
                    props: { variant: 'green' },
                    style: {
                        '& .MuiTableCell-head': {
                            backgroundColor: variable.green,
                            color: variable.white,
                            border: `1px solid ${variable.greenLight2}`,
                        },
                    },
                },
                {
                    props: { variant: 'greenLight' },
                    style: {
                        '& .MuiTableCell-head': {
                            color: variable.greenLight,
                            border: `1px solid ${variable.greenLight2}`,
                        },
                    },
                },
                {
                    props: { variant: 'greenLightWithoutBorder' },
                    style: {
                        '& .MuiTableCell-head': {
                            color: variable.greenLight,
                        },
                    },
                },
            ],
        },
        MuiTableRow: {
            variants: [
                {
                    props: { variant: 'greyLight' },
                    style: {
                        '& .MuiTableCell-root': {
                            backgroundColor: variable.greyLight,
                            color: variable.greenLight,
                            border: `1px solid ${variable.greenLight2}`,
                        },
                    },
                },
                {
                    props: { variant: 'white' },
                    style: {
                        '& .MuiTableCell-root': {
                            border: `1px solid ${variable.greenLight2}`,
                        },
                    },
                },
                {
                    props: { variant: 'greenLight' },
                    style: {
                        '& .MuiTableCell-root': {
                            color: variable.greenLight,
                            border: `1px solid ${variable.greenLight2}`,
                        },
                    },
                },
            ],
        },
        MuiTooltip: {
            styleOverrides: { tooltip: { maxWidth: 'none' } },
        },
    },
    typography: {
        h1: {
            fontSize: '2rem',
            fontWeight: 'bold',
        },
        h2: {
            fontSize: '1.2rem',
            color: variable.greenLight,
        },
        subtitle1: {
            fontSize: '0.8rem',
            paddingTop: '0.5rem',
        },
        subtitle2: {
            fontSize: '1rem',
            fontWeight: 'bold',
        },
    },
});
